import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue
} from 'react-hook-form';
import { Check, Search } from 'lucide-react';
import Input from '@components/atoms/ui-kit/input';
import Checkbox from '@components/atoms/ui-kit/checkbox';
import PhoneField from '@components/atoms/ui-kit/phone-input';
import Loader from '@components/atoms/loaders/loader';
import {
  WizardFormData,
  CustomerType,
  CUSTOMER_TYPE_OPTIONS
} from '../create-customer-wizard-types';

interface Reseller {
  id: string | number;
  name: string;
  email: string;
}

interface Props {
  register: UseFormRegister<WizardFormData>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<WizardFormData, any>;
  errors: FieldErrors<WizardFormData>;
  setValue: UseFormSetValue<WizardFormData>;
  watchedValues: WizardFormData;
  selectedCustomerType: CustomerType;
  handleCustomerTypeSelect: (type: CustomerType) => void;
  resellerSearch: string;
  setResellerSearch: (v: string) => void;
  isLoadingResellers: boolean;
  resellers: Reseller[];
  setSelectedParentResellerId: (id: number | undefined) => void;
}

export function CustomerWizardStep1({
  register,
  control,
  errors,
  setValue,
  watchedValues,
  selectedCustomerType,
  handleCustomerTypeSelect,
  resellerSearch,
  setResellerSearch,
  isLoadingResellers,
  resellers,
  setSelectedParentResellerId
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  return (
    <div className="space-y-6">
      {/* Customer Type Selection */}
      <div>
        <h4
          className="text-sm font-medium mb-3"
          style={{ color: getColour('normalText') }}
        >
          {t('customer-type')} <span className="text-red-500">*</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CUSTOMER_TYPE_OPTIONS.map(option => {
            const isSelected = selectedCustomerType === option.type;
            const Icon = option.icon;
            return (
              <button
                key={option.type}
                type="button"
                onClick={() => handleCustomerTypeSelect(option.type)}
                className="relative p-4 rounded-2xl border-2 text-left transition-all"
                style={{
                  borderColor: isSelected
                    ? option.color
                    : getColour('hairline'),
                  backgroundColor: isSelected
                    ? option.bgColor
                    : getColour('appSoft')
                }}
              >
                {isSelected && (
                  <div
                    className="absolute top-2 right-2 h-5 w-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: option.color }}
                  >
                    <Check className="h-3 w-3 text-white" />
                  </div>
                )}
                <div
                  className="h-10 w-10 rounded-2xl flex items-center justify-center mb-3"
                  style={{ backgroundColor: option.bgColor }}
                >
                  <Icon className="h-5 w-5" style={{ color: option.color }} />
                </div>
                <h3
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t(
                    option.type === 'sub_org' ? 'sub-organization' : option.type
                  )}
                </h3>
                <p
                  className="text-xs mt-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t(`${option.type}-customer-description`)}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Parent Reseller Selection for sub-orgs */}
      {selectedCustomerType === 'sub_org' && (
        <div
          className="rounded-2xl border pt-4 pb-1 px-4"
          style={{
            borderColor: getColour('hairline'),
            backgroundColor: getColour('appSoft')
          }}
        >
          <h4
            className="text-sm font-medium mb-3"
            style={{ color: getColour('normalText') }}
          >
            {t('parent-reseller')} <span className="text-red-500">*</span>
          </h4>

          <div className="relative pb-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
              style={{ color: getColour('inactiveText') }}
            />
            <input
              type="text"
              placeholder={t('search-resellers')}
              value={resellerSearch}
              onChange={e => setResellerSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm rounded-2xl border"
              style={{
                backgroundColor: getColour('firstLayer'),
                borderColor: getColour('hairline'),
                color: getColour('normalText')
              }}
            />
          </div>

          {isLoadingResellers ? (
            <div className="flex justify-center py-4">
              <Loader height={6} width={2} />
            </div>
          ) : resellers.length === 0 ? (
            <p
              className="text-sm p-3 rounded-2xl"
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('inactiveText')
              }}
            >
              {resellerSearch.trim()
                ? t('no-matching-resellers')
                : t('no-resellers-available')}
            </p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {resellers.map(reseller => (
                <button
                  key={reseller.id}
                  type="button"
                  onClick={() => {
                    const resellerId = Number(reseller.id);
                    setValue('parent_organization_id', resellerId);
                    setSelectedParentResellerId(resellerId);
                  }}
                  className="w-full px-2 py-1.5 rounded-2xl border text-left transition-colors"
                  style={{
                    borderColor:
                      watchedValues.parent_organization_id ===
                      Number(reseller.id)
                        ? getColour('activeText')
                        : getColour('hairline'),
                    backgroundColor:
                      watchedValues.parent_organization_id ===
                      Number(reseller.id)
                        ? getColour('thirdLayer')
                        : getColour('firstLayer')
                  }}
                >
                  <span
                    className="text-sm font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {reseller.name}
                  </span>
                  <span
                    className="text-xs ml-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {reseller.email}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Required Fields */}
      <div
        className="border-t pt-4"
        style={{ borderColor: getColour('hairline') }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label={t('organization-name')}
            {...register('name')}
            error={errors.name?.message}
            required
          />
          <Input
            label={t('email')}
            type="email"
            {...register('email')}
            error={errors.email?.message}
            required
          />
        </div>
      </div>

      {/* Optional Fields */}
      <div
        className="border-t pt-4"
        style={{ borderColor: getColour('hairline') }}
      >
        <h4
          className="text-sm font-medium mb-3"
          style={{ color: getColour('inactiveText') }}
        >
          {t('optional-fields')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneField
                label={t('phone')}
                value={field.value || ''}
                onChange={field.onChange}
                error={errors.phone?.message}
              />
            )}
          />
          <Input
            label={t('max-users')}
            type="number"
            min={1}
            {...register('max_users', { valueAsNumber: true })}
          />
          <div className="flex items-center justify-center mt-4">
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label={t('activate-immediately')}
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
